import { vatPercentageMutation } from "@/admin/accounts/accountsQuery.ts";
import type { GQAccountItem } from "@/admin/accounts/AccountsTable.tsx";
import { AdminModalButton } from "@/admin/AdminModalButton.tsx";
import { AInput } from "@/admin/form/Fields.tsx";
import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { Percent } from "@/components/l10n/Percent.tsx";
import { VatTextPercent } from "@/components/VatText.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";

interface VatModalModalProps {
    item: GQAccountItem;
    value: number;
}

interface VatModalInputs {
    vat: string;
}

export function VatModalButton(props: VatModalModalProps) {
    return (
        <AdminModalButton label={<Percent value={props.value} />} isDeleted={props.item.is_deleted}>
            <VatModalModalContent {...props} />
        </AdminModalButton>
    );
}

function VatModalModalContent({ item, value }: VatModalModalProps) {
    const modifyMutation = useStandardMutation(vatPercentageMutation);
    const { id } = item;

    const onSubmit: SubmitHandler<VatModalInputs> = async (inputs) => {
        const { vat } = inputs;

        await modifyMutation.mutateAsync({
            body: {
                billing_account_id: Number(id),
                vat_percentage: Number(vat),
            },
        });
    };

    const form = useForm<VatModalInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            vat: String(value),
        },
    });

    const { control } = form;

    return (
        <WModalForm
            form={form}
            onSubmit={onSubmit}
            title={
                <>
                    Set <VatTextPercent /> for billing account {id}
                </>
            }
            label="Submit"
        >
            <Controller
                control={control}
                name="vat"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WTextField
                        {...formPropsToAriaProps(props)}
                        headless
                        block
                        autoFocus
                        className="max-w-24rem"
                        wide
                        isRequired
                        label={<VatTextPercent />}
                    >
                        <AInput />
                    </WTextField>
                )}
            />
        </WModalForm>
    );
}
